import { useState, useEffect } from 'react'
import { DocumentTextIcon, PlusIcon, EyeIcon, ArrowPathIcon, TrashIcon } from '@heroicons/react/24/outline'
import { useToast } from '../../contexts/ToastContext'
import api from '../../services/api'

interface TemplateItem {
  key: string
  name: string
  paper: string
  description: string | null
  is_base: boolean
  has_override: boolean
  is_active: boolean
}

interface TemplateDetail extends TemplateItem {
  content: string
  base_content: string | null
}

export default function PrintTemplates() {
  const toast = useToast()
  const [list, setList] = useState<TemplateItem[]>([])
  const [selectedKey, setSelectedKey] = useState<string | null>(null)
  const [detail, setDetail] = useState<TemplateDetail | null>(null)
  const [content, setContent] = useState('')
  const [name, setName] = useState('')
  const [paper, setPaper] = useState('a4')
  const [saving, setSaving] = useState(false)
  const [previewing, setPreviewing] = useState(false)

  // Crear nueva
  const [creating, setCreating] = useState(false)
  const [newKey, setNewKey] = useState('')

  const fetchList = async () => {
    try {
      const { data } = await api.get('/print-templates')
      if (data.success) setList(data.data)
    } catch {
      toast.error('No se pudieron cargar las plantillas')
    }
  }

  useEffect(() => { fetchList() }, [])

  const selectTemplate = async (key: string) => {
    setCreating(false)
    try {
      const { data } = await api.get(`/print-templates/${key}`)
      if (data.success) {
        setDetail(data.data)
        setSelectedKey(key)
        setContent(data.data.content)
        setName(data.data.name)
        setPaper(data.data.paper)
      }
    } catch {
      toast.error('No se pudo cargar la plantilla')
    }
  }

  const startCreate = () => {
    setCreating(true)
    setSelectedKey(null)
    setDetail(null)
    setNewKey('')
    setName('')
    setPaper('80mm')
    setContent('<!DOCTYPE html>\n<html lang="es">\n<head>\n<meta charset="utf-8">\n<style>\n  @page { size: 80mm auto; margin: 0; }\n  body { font-family: Arial, sans-serif; padding: 3mm; margin: 0; }\n</style>\n</head>\n<body>\n  <h2>{{ $producto }}</h2>\n  <p>Lote: {{ $lote }} · Cad: {{ $caducidad }}</p>\n</body>\n</html>')
  }

  const save = async () => {
    setSaving(true)
    try {
      const { data } = creating
        ? await api.post('/print-templates', { key: newKey, name, paper, content })
        : await api.put(`/print-templates/${selectedKey}`, { name, paper, content })
      if (data.success) {
        toast.success(data.message || 'Plantilla guardada')
        await fetchList()
        if (creating) {
          setCreating(false)
          await selectTemplate(newKey)
        } else if (selectedKey) {
          await selectTemplate(selectedKey)
        }
      }
    } catch (e: any) {
      toast.error(e?.response?.data?.message || 'Error al guardar')
    } finally {
      setSaving(false)
    }
  }

  const preview = async () => {
    setPreviewing(true)
    try {
      const res = await api.post('/print-templates/preview', { content, paper }, { responseType: 'blob' })
      const url = URL.createObjectURL(res.data)
      window.open(url, '_blank')
      setTimeout(() => URL.revokeObjectURL(url), 60000)
    } catch (e: any) {
      // El cuerpo de error viene como Blob; intentamos leer el mensaje
      let msg = 'Error al previsualizar'
      try {
        const txt = await e?.response?.data?.text?.()
        if (txt) msg = JSON.parse(txt).message || msg
      } catch { /* noop */ }
      toast.error(msg)
    } finally {
      setPreviewing(false)
    }
  }

  const restoreOrDelete = async () => {
    if (!selectedKey || !detail) return
    const isBase = detail.is_base
    const msg = isBase
      ? '¿Restaurar esta plantilla a la versión base del sistema? Se perderán tus cambios.'
      : '¿Eliminar esta plantilla?'
    if (!confirm(msg)) return
    try {
      const { data } = await api.delete(`/print-templates/${selectedKey}`)
      if (data.success) {
        toast.success(data.message)
        await fetchList()
        if (isBase) {
          await selectTemplate(selectedKey)
        } else {
          setSelectedKey(null); setDetail(null)
        }
      }
    } catch (e: any) {
      toast.error(e?.response?.data?.message || 'Error')
    }
  }

  return (
    <div className="w-full px-4 sm:px-6 lg:px-8">
      <div className="sm:flex sm:items-center sm:justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <DocumentTextIcon className="h-7 w-7 text-primary-600" />
            Plantillas de impresión
          </h1>
          <p className="text-sm text-gray-500 mt-1">
            Edita las plantillas (Blade) de etiquetas e informes. Variables disponibles: <code>$producto</code>, <code>$lote</code>, <code>$caducidad</code>, <code>$ingredientes</code>, <code>$consumos</code>, <code>$alergenos</code>, <code>$extra_alergenos</code>…
          </p>
        </div>
        <button
          onClick={startCreate}
          className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 rounded-md"
        >
          <PlusIcon className="h-5 w-5" /> Nueva plantilla
        </button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Lista */}
        <div className="lg:col-span-1 space-y-2">
          {list.map(t => (
            <button
              key={t.key}
              onClick={() => selectTemplate(t.key)}
              className={`w-full text-left p-3 rounded-md border ${selectedKey === t.key ? 'border-primary-500 bg-primary-50' : 'border-gray-200 bg-white hover:bg-gray-50'}`}
            >
              <div className="font-medium text-gray-900">{t.name}</div>
              <div className="text-xs text-gray-500 font-mono">{t.key}</div>
              <div className="mt-1 flex gap-2">
                <span className="inline-flex px-2 py-0.5 text-xs rounded-full bg-gray-100 text-gray-700">{t.paper}</span>
                {t.is_base
                  ? <span className="inline-flex px-2 py-0.5 text-xs rounded-full bg-blue-100 text-blue-700">Base</span>
                  : <span className="inline-flex px-2 py-0.5 text-xs rounded-full bg-purple-100 text-purple-700">Personalizada</span>}
                {t.has_override && t.is_base && <span className="inline-flex px-2 py-0.5 text-xs rounded-full bg-amber-100 text-amber-700">Modificada</span>}
              </div>
            </button>
          ))}
        </div>

        {/* Editor */}
        <div className="lg:col-span-2">
          {(detail || creating) ? (
            <div className="bg-white border border-gray-200 rounded-md p-4 space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {creating && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Clave (única, a-z_)</label>
                    <input
                      value={newKey}
                      onChange={e => setNewKey(e.target.value)}
                      placeholder="mi_etiqueta"
                      className="w-full border border-gray-300 rounded-md px-3 py-2 font-mono text-sm"
                    />
                  </div>
                )}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Nombre</label>
                  <input
                    value={name}
                    onChange={e => setName(e.target.value)}
                    className="w-full border border-gray-300 rounded-md px-3 py-2"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Papel</label>
                  <select
                    value={paper}
                    onChange={e => setPaper(e.target.value)}
                    className="w-full border border-gray-300 rounded-md px-3 py-2"
                  >
                    <option value="a4">A4</option>
                    <option value="80mm">Térmica 80mm</option>
                    <option value="58mm">Térmica 58mm</option>
                  </select>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Plantilla (HTML + Blade)</label>
                <textarea
                  value={content}
                  onChange={e => setContent(e.target.value)}
                  spellCheck={false}
                  className="w-full border border-gray-300 rounded-md px-3 py-2 font-mono text-xs"
                  style={{ height: '420px' }}
                />
              </div>

              <div className="flex flex-wrap justify-end gap-3">
                {!creating && detail && (detail.has_override || !detail.is_base) && (
                  <button
                    onClick={restoreOrDelete}
                    className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-red-700 bg-white border border-red-300 rounded-md hover:bg-red-50"
                  >
                    {detail.is_base ? <ArrowPathIcon className="h-4 w-4" /> : <TrashIcon className="h-4 w-4" />}
                    {detail.is_base ? 'Restaurar base' : 'Eliminar'}
                  </button>
                )}
                <button
                  onClick={preview}
                  disabled={previewing}
                  className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50"
                >
                  <EyeIcon className="h-4 w-4" /> {previewing ? 'Generando…' : 'Previsualizar'}
                </button>
                <button
                  onClick={save}
                  disabled={saving || (creating && !newKey)}
                  className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-primary-600 rounded-md hover:bg-primary-700 disabled:opacity-50"
                >
                  {saving ? 'Guardando…' : 'Guardar'}
                </button>
              </div>
            </div>
          ) : (
            <div className="flex items-center justify-center h-64 text-gray-400 border border-dashed border-gray-300 rounded-md">
              Selecciona una plantilla o crea una nueva
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
